import React, { useState } from 'react';
import { isEmpty } from 'lodash';

import dayjs from 'utils/dayjs';
import { toCurrency } from '../../helpers/numberHelper';
import { getDatesArrayForRange } from '../../helpers/dateHelper';
import { colorByIndex, numberTickFormatter } from '../common/charts/chartUtils';
import Select from 'components/forms/Select';
import OtaInsightExportModule from './otaInsightExportModule';

import Spinner from '../UI/spinner';
import LineChart from './otaInsightsLineChart';

import './otaInsights.style.css';

const { Option } = Select;

const OTAInsights = ({
    startDate,
    endDate,
    data = null,
    demand = {},
    otaCompsets = [],
    otaHotelId,
    canViewOTACompsets,
    propertyId,
}) => {
    // Account global config in the future
    const apiDateFormat = 'YYYY-MM-DD';
    const displayDateFormat = 'ddd DD MMM YY';
    const [selectedCompSet, setSelectedCompSet] = useState(
        isEmpty(otaCompsets) ? 1 : otaCompsets?.find((compset) => compset.default)?.id || 1,
    );

    // Return a Moment range of date between given props dates
    const getDaysForCurrentDateRange = () => getDatesArrayForRange(startDate, endDate);

    const removeExtraColumns = (d) =>
        d.map((row) => {
            const { rowLabel, hasData, ...rest } = row;
            return { date: rowLabel, ...rest };
        });

    // Format data for a grid  (i.e. CSV export)
    const getParsedData = () => {
        const d = data.map((row) => {
            const newObj = { rowLabel: row.hotelName, hasData: false };
            getDaysForCurrentDateRange().forEach((dayDate) => {
                const dayData = row[dayDate.format(apiDateFormat)];
                const dateKey = dayDate.format(displayDateFormat);
                const value = dayData ? dayData.value : 0;
                if (value > 0) newObj.hasData = true;
                newObj[dateKey] = toCurrency(value);
            });
            return newObj;
        });

        return removeExtraColumns(d);
    };

    // filter the ota data by the selected compset
    const filterDataBySelectedCompset = () => {
        if (!isEmpty(otaCompsets) && selectedCompSet) {
            const selectedCompSetIds = otaCompsets
                .find((set) => set.id === selectedCompSet)
                .competitors.map((competitor) => competitor.id);
            if (otaHotelId && !selectedCompSetIds.includes(Number(otaHotelId))) {
                selectedCompSetIds.push(Number(otaHotelId));
            }
            const compSet = data.filter((item) => selectedCompSetIds.includes(item.hotelId));
            return compSet;
        }
        return data;
    };

    // Format data to be displayed in the graph
    const getGraphData = () =>
        getDaysForCurrentDateRange().map((dayDate) => {
            const object = {
                dateKey: dayDate.format(displayDateFormat),
                date: dayDate,
            };
            filterDataBySelectedCompset().forEach((hotelRow) => {
                const apiDate = dayDate.format(apiDateFormat);
                const currency = hotelRow[apiDate] ? hotelRow[apiDate].currency : '';
                const message = hotelRow[apiDate] ? hotelRow[apiDate].message : '';
                const roomName = hotelRow[apiDate] ? hotelRow[apiDate].roomName : '';
                const roomType = hotelRow[apiDate] ? hotelRow[apiDate].roomType : '';
                const mealTypeIncluded = hotelRow[apiDate] ? hotelRow[apiDate].mealTypeIncluded : '';
                object[hotelRow.hotelName] = {
                    price: hotelRow[apiDate] ? hotelRow[apiDate].value : 0,
                    currency,
                    message,
                    roomName,
                    roomType,
                    mealTypeIncluded,
                };
            });
            return object;
        });

    const renderLineChart = () => {
        const graphData = getGraphData();
        const lines = filterDataBySelectedCompset().map((hotelRow, index) => ({
            dataKey: (row) => row[hotelRow.hotelName]?.price || undefined,
            name: hotelRow.hotelName,
            color: colorByIndex(index),
        }));
        return (
            <LineChart
                data={graphData}
                demand={demand}
                lines={lines}
                dataKey="dateKey"
                yAxisTickFormatter={numberTickFormatter}
                refLineValue={dayjs().format(displayDateFormat)}
            />
        );
    };

    const changeCompSet = (id) => {
        setSelectedCompSet(id);
    };

    if (!data) return <Spinner />;
    const parsedData = getParsedData();

    if (parsedData.length === 0) {
        return <div className="OTAInsightsNoData">No existing data for selected date range.</div>;
    }

    const start = startDate.format(apiDateFormat);
    const end = endDate.format(apiDateFormat);

    return (
        <div>
            {!isEmpty(otaCompsets) && canViewOTACompsets && (
                <div className="OTAInsightsCompSet">
                    Competitor Set
                    <Select
                        id="ota-compsets"
                        value={selectedCompSet}
                        onChange={changeCompSet}
                        style={{ minWidth: 150, paddingLeft: 5 }}
                    >
                        {otaCompsets.map((compset) => (
                            <Option key={compset.id} value={compset.id}>
                                {compset.name}
                            </Option>
                        ))}
                    </Select>
                </div>
            )}
            <div className="OTAInsightsChart">{renderLineChart()}</div>
            <div className="OTAInsightsFooter">
                <OtaInsightExportModule start={start} end={end} propertyId={propertyId} />
                <span>
                    Showing data from <strong>brand.com</strong>
                </span>
            </div>
        </div>
    );
};

export default OTAInsights;
